'use client'

import { useEffect, useRef, useState } from 'react'
import { usePersistStore } from '@/store/persist.store'
import { useConfig } from '@/context/ConfigProvider'
import { useChatStore } from '@/store/chat.store'
import {
  contrastingText,
  normalizeWidgetAppearance,
  widgetColorVariables,
  widgetPalette,
  type WidgetAppearance
} from '@/lib/widget-appearance'

export default function WidgetAppearanceStyles({ previewAppearance }: { previewAppearance?: WidgetAppearance }) {
  const config = useConfig()
  const persistedSiteId = usePersistStore(state => state.siteId)
  const siteId = config.siteId || persistedSiteId
  const initialized = useRef(false)
  const isOpen = useChatStore(state => state.isChatIframeShown)
  const [loaded, setLoaded] = useState<{ siteId: string; appearance: WidgetAppearance } | null>(() =>
    config.siteId && config.appearance ? { siteId: config.siteId, appearance: config.appearance } : null
  )
  const appearance = previewAppearance ?? (loaded?.siteId === siteId ? loaded.appearance : {})

  useEffect(() => {
    if (previewAppearance !== undefined || !siteId) return
    if (!initialized.current) {
      initialized.current = true
      if (config.siteId === siteId && config.appearance) return
    } else if (!isOpen) return
    const controller = new AbortController()
    fetch(`/api/widget-appearance?site_id=${encodeURIComponent(siteId)}`, {
      signal: controller.signal,
      cache: 'no-store'
    })
      .then(response => {
        if (!response.ok) throw new Error('Appearance unavailable')

        return response.json()
      })
      .then(body => {
        if (!controller.signal.aborted) setLoaded({ siteId, appearance: normalizeWidgetAppearance(body.appearance) })
      })
      .catch(() => {
        // Keep the last known palette on transient errors; never flash defaults.
      })

    return () => controller.abort()
  }, [siteId, isOpen, previewAppearance, config.siteId, config.appearance])

  useEffect(() => {
    if (previewAppearance !== undefined || loaded?.siteId !== siteId) return

    const sendLauncherColors = () => {
      const mode = document.documentElement.classList.contains('dark') ? 'dark' : 'light'
      const current = loaded?.siteId === siteId ? loaded.appearance : {}
      const palette = widgetPalette(current, mode)
      const customized = Object.keys(current[mode] ?? {}).length > 0
      window.parent.postMessage(
        {
          type: 'WIDGET_APPEARANCE',
          siteId,
          colors: customized ? { background: palette.header, foreground: contrastingText(palette.header) } : null
        },
        '*'
      )
    }
    sendLauncherColors()
    const observer = new MutationObserver(sendLauncherColors)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    return () => observer.disconnect()
  }, [loaded, siteId, previewAppearance])

  const rules = (['light', 'dark'] as const)
    .map(mode => {
      const declarations = Object.entries(widgetColorVariables(appearance, mode))
        .map(([key, value]) => `${key}:${value}`)
        .join(';')

      return `${mode === 'dark' ? ':root.dark' : ':root:not(.dark)'}{${declarations}}`
    })
    .join('\n')

  return <style>{rules}</style>
}
